import React, { useState, useEffect } from 'react';
import { getRoadmaps, deleteRoadmap, getImportableSyllabuses, importSyllabus, toggleRoadmapUserAssigned, updateRoadmap } from '../services/api';
import { getCache, setCache, invalidateCache } from '../services/store';

const CACHE_KEY = 'roadmaps';

function RoadmapsList({ onNew, onEdit }) {
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [syllabuses, setSyllabuses] = useState([]);
  const [selectedSyllabusId, setSelectedSyllabusId] = useState('');
  const [importing, setImporting] = useState(false);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchRoadmaps();
  }, []);

  useEffect(() => {
    if (showImportModal) {
      fetchSyllabuses();
    }
  }, [showImportModal]);

  const fetchSyllabuses = async () => {
    try {
      const data = await getImportableSyllabuses();
      setSyllabuses(data);
      if (data.length > 0) {
        setSelectedSyllabusId(data[0].id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleImport = async () => {
    if (!selectedSyllabusId) return;
    try {
      setImporting(true);
      await importSyllabus(selectedSyllabusId);
      setShowImportModal(false);
      invalidateCache(CACHE_KEY);
      fetchRoadmaps(true);
    } catch (err) {
      alert('Failed to import syllabus: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const fetchRoadmaps = async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCache(CACHE_KEY);
      if (cached) {
        setRoadmaps(cached);
        return;
      }
    }
    try {
      setLoading(true);
      const data = await getRoadmaps();
      setCache(CACHE_KEY, data);
      setRoadmaps(data);
    } catch (err) {
      setError('Failed to load roadmaps');
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this roadmap?')) {
      return;
    }

    try {
      await deleteRoadmap(id);
      invalidateCache(CACHE_KEY);
      fetchRoadmaps(true);
    } catch (err) {
      alert('Failed to delete roadmap');
    }
  };

  const handleToggleAssigned = async (id, currentValue) => {
    try {
      await toggleRoadmapUserAssigned(id, !currentValue);
      setRoadmaps(roadmaps.map(r => 
        r.id === id ? { ...r, userAssignedRoadmap: !currentValue } : r
      ));
    } catch (err) {
      alert('Failed to update roadmap assignment status');
    }
  };

  const handleFieldUpdate = async (id, fieldName, value) => {
    try {
      await updateRoadmap(id, { [fieldName]: value });
      setRoadmaps(roadmaps.map(r => 
        r.id === id ? { ...r, [fieldName]: value } : r
      ));
    } catch (err) {
      alert('Failed to update roadmap field: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span className="text-gray-400 ml-1">⇅</span>;
    return sortDirection === 'asc' ? <span className="text-indigo-600 ml-1">▲</span> : <span className="text-indigo-600 ml-1">▼</span>;
  };

  const sortedRoadmaps = [...roadmaps].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (valA === undefined || valA === null) valA = '';
    if (valB === undefined || valB === null) valB = '';

    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Roadmaps</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            Import Syllabus
          </button>
          <button
            onClick={onNew}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            + New Roadmap
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {roadmaps.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">No roadmaps yet</p>
          <button
            onClick={onNew}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Create First Roadmap
          </button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  onClick={() => handleSort('id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  ID {renderSortIcon('id')}
                </th>
                <th 
                  onClick={() => handleSort('mainTopic')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  Main Topic {renderSortIcon('mainTopic')}
                </th>
                <th 
                  onClick={() => handleSort('category')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  Category {renderSortIcon('category')}
                </th>
                <th 
                  onClick={() => handleSort('displayOrder')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  Order {renderSortIcon('displayOrder')}
                </th>
                <th 
                  onClick={() => handleSort('userAssignedRoadmap')}
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  UserAssignedRoadmap {renderSortIcon('userAssignedRoadmap')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRoadmaps
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((roadmap) => (
                  <tr key={roadmap.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {roadmap.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {roadmap.mainTopic}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={roadmap.category || 'TECHNICAL'}
                        onChange={(e) => handleFieldUpdate(roadmap.id, 'category', e.target.value)}
                        className="p-1 border border-gray-300 rounded bg-white text-gray-950 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="TECHNICAL">TECHNICAL</option>
                        <option value="DSA">DSA</option>
                        <option value="NON_TECHNICAL">NON_TECHNICAL</option>
                        <option value="COMMON">COMMON</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        value={roadmap.displayOrder !== undefined ? roadmap.displayOrder : 0}
                        onChange={(e) => handleFieldUpdate(roadmap.id, 'displayOrder', parseInt(e.target.value) || 0)}
                        className="w-16 p-1 border border-gray-300 rounded text-sm text-gray-950 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={roadmap.userAssignedRoadmap || false}
                        onChange={() => handleToggleAssigned(roadmap.id, roadmap.userAssignedRoadmap)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(roadmap.id)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(roadmap.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {roadmaps.length > itemsPerPage && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, Math.ceil(roadmaps.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(roadmaps.length / itemsPerPage)}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, roadmaps.length)}
                    </span>{' '}
                    of <span className="font-medium">{roadmaps.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span>Previous</span>
                    </button>
                    {/* Simple page indicator for now */}
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {currentPage} of {Math.ceil(roadmaps.length / itemsPerPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, Math.ceil(roadmaps.length / itemsPerPage)))}
                      disabled={currentPage === Math.ceil(roadmaps.length / itemsPerPage)}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span>Next</span>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Import Syllabus from Interview Trainer</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Syllabus</label>
              {syllabuses.length === 0 ? (
                <p className="text-sm text-gray-500">No syllabuses found or failed to load.</p>
              ) : (
                <select
                  value={selectedSyllabusId}
                  onChange={(e) => setSelectedSyllabusId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white text-gray-900"
                >
                  {syllabuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.topic} (ID: {s.id})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                disabled={importing}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || !selectedSyllabusId}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center"
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoadmapsList;
